import axios, { type AxiosResponseTransformer } from 'axios';
import { useCatalogoStore } from '@/features/catalogo/store/store';
import { useLoginAdminStore } from '@/features/login-admin/store';
import { resetLoginStore, useLoginStore } from '@/features/login/store';
import { getLogin } from '@/features/login/util';
import { router } from '@/router/Router';
import { API_URL } from '@/std/api/url';
import { TooManyRequestsError } from './httpError';
import { getUserSessionSignal } from './userSessionSignal';
import { isRouteAdmin, isRouteCatalogo } from './util';

const transformResponse: AxiosResponseTransformer = function (data, headers, status) {
    if (status === 429) {
        throw new TooManyRequestsError();
    }

    if (status === 401) {
        return undefined;
    }

    const transformers = axios.defaults.transformResponse;
    for (const transform of Array.isArray(transformers) ? transformers : [transformers]) {
        if (transform) {
            data = transform.call(this, data, headers, status);
        }
    }

    return data;
};

/** @deprecated Instância do axios sem o bearer token */
export const axiosWithoutToken = axios.create({
    baseURL: API_URL,
    headers: { 'Content-Type': 'application/json' },
    transformResponse,
});

/** @deprecated Instância do axios com o bearer token */
export const axiosWithToken = axios.create({
    baseURL: API_URL,
    headers: { 'Content-Type': 'application/json' },
    transformResponse,
});

const requestSessionSignals = new WeakMap<object, AbortSignal>();

// o código abaixo serve para lidar quando o usuário está sem token ou com token expirado

useLoginStore.subscribe((state, previous) => {
    if (previous.login && !state.login) {
        delete axiosWithToken.defaults.headers.common.Authorization;
    }
});

function redirectToLogin(): void {
    resetLoginStore();
    void router.navigate({ ignoreBlocker: true, replace: true, to: '/auth/login' });
}

axiosWithToken.interceptors.request.use((request) => {
    if (!request.headers) {
        return request;
    }

    if (isRouteAdmin()) {
        const token = useLoginAdminStore.getState().loginAdmin?.token;
        if (!token) {
            return request;
        }

        request.headers.Authorization = `Bearer ${token}`;
        return request;
    }

    if (isRouteCatalogo()) {
        const token = useCatalogoStore.getState().getEmpresa().login?.token;
        if (!token) {
            return request;
        }

        request.headers.Authorization = `Bearer ${token}`;
        return request;
    }

    const token = getLogin()?.token;
    if (!token) {
        redirectToLogin();
        throw new axios.CanceledError('Sessão encerrada');
    }

    const sessionSignal = getUserSessionSignal();
    requestSessionSignals.set(request, sessionSignal);
    request.signal = request.signal
        ? AbortSignal.any([request.signal as AbortSignal, sessionSignal])
        : sessionSignal;
    request.headers.Authorization = `Bearer ${token}`;
    return request;
});

axiosWithToken.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (axios.isCancel(error) || error instanceof TooManyRequestsError) {
            throw error;
        }

        if (!isRouteAdmin() && !isRouteCatalogo() && !getLogin()?.token) {
            redirectToLogin();
            throw new axios.CanceledError('Sessão encerrada');
        }

        if (error.response?.status !== 401) {
            return error;
        }

        if (error.response?.status === 401) {
            if (error.config.url === 'Login/RenovarToken') {
                return;
            }

            try {
                const response = await axiosWithToken.post('Login/RenovarToken');

                if (response.status === 401) {
                    throw new Error();
                }

                if (!response.data.token) {
                    throw new Error();
                }

                axiosWithToken.defaults.headers.common.Authorization = `Bearer ${response.data.token}`;
                error.hasRefreshedToken = true;
            } catch {
                if (requestSessionSignals.get(error.config)?.aborted) {
                    return error;
                }
                redirectToLogin();
                return error;
            }
        }
    },
);
